<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWebOrderStore } from 'dashboard/stores/webOrder';
import { useOnVisible } from '../composables/useOnVisible';
import { formatLocalDateKey } from 'dashboard/routes/dashboard/webOrder/helpers/agentOrderHelpers';
import {
  DATE_RANGE_TYPES,
  getActiveDateRange,
  overviewDateRanges,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import LineChart from 'shared/components/charts/LineChart.vue';

const FIRST_TIME_COLOR = '#3b82f6';
const RETURNING_COLOR = '#ef4444';
const APPROVED_COLOR = '#4c7046';
const CANCELLED_COLOR = '#e5484d';
const ON_HOLD_COLOR = '#d97706';
const ARRIVALS_COLOR = '#4c7046';
const IN_TRANSIT_COLOR = '#2563eb';
const DELIVERED_COLOR = '#4c7046';
const RETURNED_COLOR = '#e5484d';

const { t } = useI18n();
const store = useWebOrderStore();
const { target, visible } = useOnVisible();

const i18nKey = 'OVERVIEW_REPORTS.WEB_ORDER';
const initialRange = getActiveDateRange(
  DATE_RANGE_TYPES.LAST_7_DAYS,
  new Date()
);
const dateRange = ref([initialRange.start, initialRange.end]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const mixKey = ref('arrivals');
const businessMixKey = ref('orders');

const mixOptions = computed(() => [
  { value: 'arrivals', label: t(`${i18nKey}.MIX_ARRIVALS`) },
  { value: 'actions', label: t(`${i18nKey}.MIX_ACTIONS`) },
  { value: 'fulfillment', label: t(`${i18nKey}.MIX_FULFILLMENT`) },
]);

const businessMixOptions = computed(() => [
  { value: 'orders', label: t(`${i18nKey}.MIX_ORDERS`) },
  { value: 'upsell', label: t(`${i18nKey}.MIX_UPSELL`) },
  { value: 'outcome', label: t(`${i18nKey}.MIX_OUTCOME`) },
]);

const mixLabel = computed(
  () =>
    mixOptions.value.find(option => option.value === mixKey.value)?.label || ''
);

const businessMixLabel = computed(
  () =>
    businessMixOptions.value.find(
      option => option.value === businessMixKey.value
    )?.label || ''
);

const analytics = computed(() => store.agentOverviewAnalytics || {});
const isLoading = computed(() => Boolean(store.loading?.overviewAnalytics));
const firstTime = computed(() => analytics.value.customers?.first_time || {});
const returning = computed(() => analytics.value.customers?.returning || {});
const dailyBusiness = computed(() => analytics.value.daily_business || {});
const dailyOrders = computed(() => dailyBusiness.value.orders || {});
const dailyRevenue = computed(() => dailyBusiness.value.revenue || {});
const dailyUpsell = computed(() => dailyBusiness.value.upsell || {});
const dailyRegular = computed(() => dailyBusiness.value.regular || {});
const dailyDelivered = computed(() => dailyBusiness.value.delivered || {});
const dailyReturned = computed(() => dailyBusiness.value.returned || {});

const rangeKey = computed(() => {
  const from = formatLocalDateKey(dateRange.value?.[0]);
  const to = formatLocalDateKey(dateRange.value?.[1]);
  return `${from}-${to}`;
});

const percentChange = (current, previous) => {
  const now = Number(current) || 0;
  const before = Number(previous) || 0;
  if (!before) return now > 0 ? 100 : 0;
  return Math.round(((now - before) / before) * 100);
};

const firstTimeTrend = computed(() =>
  percentChange(firstTime.value.total, firstTime.value.previous_total)
);
const returningTrend = computed(() =>
  percentChange(returning.value.total, returning.value.previous_total)
);
const ordersTrend = computed(() =>
  percentChange(dailyOrders.value.total, dailyOrders.value.previous_total)
);
const revenueTrend = computed(() =>
  percentChange(dailyRevenue.value.total, dailyRevenue.value.previous_total)
);
const upsellTrend = computed(() =>
  percentChange(dailyUpsell.value.total, dailyUpsell.value.previous_total)
);
const regularTrend = computed(() =>
  percentChange(dailyRegular.value.total, dailyRegular.value.previous_total)
);
const deliveredTrend = computed(() =>
  percentChange(dailyDelivered.value.total, dailyDelivered.value.previous_total)
);
const returnedTrend = computed(() =>
  percentChange(dailyReturned.value.total, dailyReturned.value.previous_total)
);

const trendClass = value => (value < 0 ? 'text-n-ruby-9' : 'text-n-teal-10');

const formatDayLabel = dateKey => {
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return String(date.getDate());
};

const formatHourLabel = hour => {
  const value = Number(hour);
  if (value === 0) return '12 AM';
  if (value === 12) return '12 PM';
  if (value < 12) return `${value} AM`;
  return `${value - 12} PM`;
};

const formatFullDate = dateKey => {
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRevenue = value =>
  (Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const lineDefaults = (color, values = []) => ({
  type: 'line',
  borderColor: color,
  backgroundColor: `${color}22`,
  borderWidth: 2.5,
  pointRadius: values.length <= 7 ? 3 : 0,
  pointHoverRadius: 5,
  pointHoverBackgroundColor: color,
  tension: 0.4,
  fill: true,
});

const seriesValues = (series = []) =>
  (series || []).map(point => Number(point.value) || 0);
const hourlyLabels = (series = []) =>
  (series || []).map(point => formatHourLabel(point.hour));
const dailyLabels = (series = []) =>
  (series || []).map(point => formatDayLabel(point.date));

const lineDataset = (series, color, label) => {
  const data = seriesValues(series);
  return {
    ...lineDefaults(color, data),
    label,
    data,
  };
};

const tooltipByDate = series => ({
  plugins: {
    tooltip: {
      callbacks: {
        title: items => {
          const index = items[0]?.dataIndex;
          const dateKey = series?.[index]?.date;
          return dateKey ? formatFullDate(dateKey) : '';
        },
      },
    },
  },
});

const customerCollection = computed(() => {
  const series = firstTime.value.series || [];
  return {
    labels: dailyLabels(series),
    datasets: [
      lineDataset(series, FIRST_TIME_COLOR, t(`${i18nKey}.FIRST_TIME`)),
      lineDataset(
        returning.value.series,
        RETURNING_COLOR,
        t(`${i18nKey}.RETURNING`)
      ),
    ],
  };
});

const mixCollection = computed(() => {
  if (mixKey.value === 'actions') {
    const approved = analytics.value.hourly_actions?.approved || [];
    return {
      labels: hourlyLabels(approved),
      datasets: [
        lineDataset(approved, APPROVED_COLOR, t(`${i18nKey}.APPROVED`)),
        lineDataset(
          analytics.value.hourly_actions?.cancelled,
          CANCELLED_COLOR,
          t(`${i18nKey}.CANCELLED`)
        ),
        lineDataset(
          analytics.value.hourly_actions?.on_hold,
          ON_HOLD_COLOR,
          t(`${i18nKey}.ON_HOLD`)
        ),
      ],
    };
  }

  if (mixKey.value === 'fulfillment') {
    const inTransit = analytics.value.hourly_fulfillment?.in_transit || [];
    return {
      labels: hourlyLabels(inTransit),
      datasets: [
        lineDataset(inTransit, IN_TRANSIT_COLOR, t(`${i18nKey}.IN_TRANSIT`)),
        lineDataset(
          analytics.value.hourly_fulfillment?.delivered,
          DELIVERED_COLOR,
          t(`${i18nKey}.DELIVERED`)
        ),
        lineDataset(
          analytics.value.hourly_fulfillment?.returned,
          RETURNED_COLOR,
          t(`${i18nKey}.RETURNED`)
        ),
      ],
    };
  }

  const arrivals = analytics.value.hourly_orders || [];
  return {
    labels: hourlyLabels(arrivals),
    datasets: [lineDataset(arrivals, ARRIVALS_COLOR, t(`${i18nKey}.ORDERS`))],
  };
});

const dailyPrimarySeries = computed(() => {
  if (businessMixKey.value === 'upsell') {
    return dailyUpsell.value.series || [];
  }
  if (businessMixKey.value === 'outcome') {
    return dailyDelivered.value.series || [];
  }
  return dailyOrders.value.series || [];
});

const businessCollection = computed(() => {
  if (businessMixKey.value === 'upsell') {
    return {
      labels: dailyLabels(dailyPrimarySeries.value),
      datasets: [
        lineDataset(
          dailyUpsell.value.series,
          APPROVED_COLOR,
          t(`${i18nKey}.UPSELL`)
        ),
        lineDataset(
          dailyRegular.value.series,
          FIRST_TIME_COLOR,
          t(`${i18nKey}.REGULAR`)
        ),
      ],
    };
  }

  if (businessMixKey.value === 'outcome') {
    return {
      labels: dailyLabels(dailyPrimarySeries.value),
      datasets: [
        lineDataset(
          dailyDelivered.value.series,
          DELIVERED_COLOR,
          t(`${i18nKey}.DELIVERED`)
        ),
        lineDataset(
          dailyReturned.value.series,
          RETURNED_COLOR,
          t(`${i18nKey}.RETURNED`)
        ),
      ],
    };
  }

  return {
    labels: dailyLabels(dailyPrimarySeries.value),
    datasets: [
      lineDataset(
        dailyOrders.value.series,
        ARRIVALS_COLOR,
        t(`${i18nKey}.ORDERS`)
      ),
    ],
  };
});

const customerChartOptions = computed(() =>
  tooltipByDate(firstTime.value.series)
);

const legendOptions = display => ({
  display,
  position: 'top',
  align: 'end',
  labels: {
    boxWidth: 8,
    boxHeight: 8,
    usePointStyle: true,
    pointStyle: 'circle',
    color: '#64748b',
  },
});

const businessChartOptions = computed(() => {
  const dated = tooltipByDate(dailyPrimarySeries.value);
  const showLegend = businessMixKey.value !== 'orders';
  return {
    plugins: {
      ...dated.plugins,
      legend: legendOptions(showLegend),
      tooltip: {
        ...dated.plugins.tooltip,
        displayColors: showLegend,
      },
    },
  };
});

const mixChartOptions = computed(() => ({
  plugins: {
    legend: legendOptions(mixKey.value !== 'arrivals'),
    tooltip: {
      displayColors: mixKey.value !== 'arrivals',
    },
  },
}));

const mixDescription = computed(() => {
  if (mixKey.value === 'actions') return t(`${i18nKey}.MIX_ACTIONS_HINT`);
  if (mixKey.value === 'fulfillment') {
    return t(`${i18nKey}.MIX_FULFILLMENT_HINT`);
  }
  return t(`${i18nKey}.MIX_ARRIVALS_HINT`);
});

const businessDescription = computed(() => {
  if (businessMixKey.value === 'upsell') {
    return t(`${i18nKey}.MIX_UPSELL_HINT`);
  }
  if (businessMixKey.value === 'outcome') {
    return t(`${i18nKey}.MIX_OUTCOME_HINT`);
  }
  return t(`${i18nKey}.MIX_ORDERS_HINT`);
});

const businessStat = (key, label, display, trend, dotClass = '') => ({
  key,
  label,
  display,
  trend,
  dotClass,
});

const businessStats = computed(() => {
  if (businessMixKey.value === 'upsell') {
    return [
      businessStat(
        'upsell',
        t(`${i18nKey}.UPSELL`),
        Number(dailyUpsell.value.total) || 0,
        upsellTrend.value,
        'bg-n-teal-9'
      ),
      businessStat(
        'regular',
        t(`${i18nKey}.REGULAR`),
        Number(dailyRegular.value.total) || 0,
        regularTrend.value,
        'bg-n-blue-9'
      ),
    ];
  }

  if (businessMixKey.value === 'outcome') {
    return [
      businessStat(
        'delivered',
        t(`${i18nKey}.DELIVERED`),
        Number(dailyDelivered.value.total) || 0,
        deliveredTrend.value,
        'bg-n-teal-9'
      ),
      businessStat(
        'returned',
        t(`${i18nKey}.RETURNED`),
        Number(dailyReturned.value.total) || 0,
        returnedTrend.value,
        'bg-n-ruby-9'
      ),
    ];
  }

  return [
    businessStat(
      'orders',
      t(`${i18nKey}.ORDERS`),
      Number(dailyOrders.value.total) || 0,
      ordersTrend.value,
      'bg-n-teal-9'
    ),
    businessStat(
      'revenue',
      t(`${i18nKey}.REVENUE`),
      formatRevenue(dailyRevenue.value.total),
      revenueTrend.value
    ),
  ];
});

watch(
  [dateRange, visible],
  () => {
    if (!visible.value) return;
    const [start, end] = dateRange.value || [];
    const from = formatLocalDateKey(start);
    const to = formatLocalDateKey(end);
    if (!from || !to) return;
    store.fetchAgentOverviewAnalytics({ from, to }).catch(() => {});
  },
  { immediate: true, deep: true }
);

const onDateRangeChanged = value => {
  const [startDate, endDate, nextRangeType] = value || [];
  dateRange.value = [startDate, endDate];
  rangeType.value = nextRangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
};
</script>

<template>
  <div ref="target" class="flex w-full min-w-0 flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <p class="text-sm text-n-slate-11">
        {{ t(`${i18nKey}.RANGE_HINT`) }}
      </p>
      <WootDatePicker
        v-model:date-range="dateRange"
        v-model:range-type="rangeType"
        compact
        align="end"
        :ranges="overviewDateRanges"
        @date-range-changed="onDateRangeChanged"
      />
    </div>
    <section
      class="w-full min-w-0 rounded-xl bg-n-solid-2 px-6 py-5 shadow outline outline-1 outline-n-container"
    >
      <div class="mb-6">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t(`${i18nKey}.CUSTOMERS_TITLE`) }}
        </h3>
        <div class="mt-4 flex flex-wrap gap-8">
          <div>
            <p class="flex items-center gap-2 text-xs text-n-slate-11">
              <span class="size-2 rounded-full bg-n-blue-9" />
              {{ t(`${i18nKey}.FIRST_TIME`) }}
            </p>
            <p class="mt-1 flex items-end gap-2">
              <span class="text-2xl font-semibold text-n-slate-12">
                {{ Number(firstTime.total) || 0 }}
              </span>
              <span
                v-if="firstTimeTrend"
                class="mb-0.5 flex items-center text-xs font-medium"
                :class="trendClass(firstTimeTrend)"
              >
                <span
                  class="mr-1 h-0 w-0 border-x-4 border-x-transparent"
                  :class="
                    firstTimeTrend < 0
                      ? 'border-t-[8px] border-t-current'
                      : 'border-b-[8px] border-b-current'
                  "
                />
                {{ Math.abs(firstTimeTrend) }}%
              </span>
            </p>
          </div>
          <div>
            <p class="flex items-center gap-2 text-xs text-n-slate-11">
              <span class="size-2 rounded-full bg-n-ruby-9" />
              {{ t(`${i18nKey}.RETURNING`) }}
            </p>
            <p class="mt-1 flex items-end gap-2">
              <span class="text-2xl font-semibold text-n-slate-12">
                {{ Number(returning.total) || 0 }}
              </span>
              <span
                v-if="returningTrend"
                class="mb-0.5 flex items-center text-xs font-medium"
                :class="trendClass(returningTrend)"
              >
                <span
                  class="mr-1 h-0 w-0 border-x-4 border-x-transparent"
                  :class="
                    returningTrend < 0
                      ? 'border-t-[8px] border-t-current'
                      : 'border-b-[8px] border-b-current'
                  "
                />
                {{ Math.abs(returningTrend) }}%
              </span>
            </p>
          </div>
        </div>
      </div>
      <div class="relative h-72">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-n-solid-2/70"
        >
          <Spinner />
        </div>
        <LineChart
          :key="`customers-${rangeKey}`"
          :collection="customerCollection"
          :chart-options="customerChartOptions"
        />
      </div>
    </section>

    <section
      class="w-full min-w-0 rounded-xl bg-n-solid-2 px-6 py-5 shadow outline outline-1 outline-n-container"
    >
      <div class="mb-6 flex items-start justify-between gap-3">
        <div>
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ t(`${i18nKey}.MIX_TITLE`) }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ mixDescription }}
          </p>
        </div>
        <SelectMenu
          v-model="mixKey"
          :options="mixOptions"
          :label="mixLabel"
          sub-menu-position="bottom"
        />
      </div>
      <div class="relative h-72">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-n-solid-2/70"
        >
          <Spinner />
        </div>
        <LineChart
          :key="`mix-${mixKey}-${rangeKey}`"
          :collection="mixCollection"
          :chart-options="mixChartOptions"
        />
      </div>
    </section>

    <section
      class="w-full min-w-0 rounded-xl bg-n-solid-2 px-6 py-5 shadow outline outline-1 outline-n-container"
    >
      <div class="mb-6 flex items-start justify-between gap-3">
        <div>
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ t(`${i18nKey}.BUSINESS_TITLE`) }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ businessDescription }}
          </p>
          <div class="mt-4 flex flex-wrap gap-8">
            <div v-for="stat in businessStats" :key="stat.key">
              <p class="flex items-center gap-2 text-xs text-n-slate-11">
                <span
                  v-if="stat.dotClass"
                  class="size-2 rounded-full"
                  :class="stat.dotClass"
                />
                {{ stat.label }}
              </p>
              <p class="mt-1 flex items-end gap-2">
                <span class="text-2xl font-semibold text-n-slate-12">
                  {{ stat.display }}
                </span>
                <span
                  v-if="stat.trend"
                  class="mb-0.5 flex items-center text-xs font-medium"
                  :class="trendClass(stat.trend)"
                >
                  <span
                    class="mr-1 h-0 w-0 border-x-4 border-x-transparent"
                    :class="
                      stat.trend < 0
                        ? 'border-t-[8px] border-t-current'
                        : 'border-b-[8px] border-b-current'
                    "
                  />
                  {{ Math.abs(stat.trend) }}%
                </span>
              </p>
            </div>
          </div>
        </div>
        <SelectMenu
          v-model="businessMixKey"
          :options="businessMixOptions"
          :label="businessMixLabel"
          sub-menu-position="bottom"
        />
      </div>
      <div class="relative h-72">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-n-solid-2/70"
        >
          <Spinner />
        </div>
        <LineChart
          :key="`business-${businessMixKey}-${rangeKey}`"
          :collection="businessCollection"
          :chart-options="businessChartOptions"
        />
      </div>
    </section>
  </div>
</template>
